import moment from 'moment';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ConfirmModal from '@/src/components/common/Modal/ConfirmModal/ConfirmModal';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { useRouterQuery } from '@/src/hooks/useRouterQuery';
import { useToggle } from '@/src/hooks/useToggle';
import { checkAccess, INTERNAL_USER_ROLES } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import { getConnectQuickBooksUrl } from './api/connectQuickbooks';
import { useDisconnectQuickbooks } from './api/disconnectQuickbooks';

import { AppConfigType } from '../../AppList/AppList';
import { useConfirmDisconnect } from '../../useConfirmDisconnect';
import AppCard from '../AppCard';

const QUERY_PARAM_SUCCESS = 'quickbooks_connected_successfully';

export type QuickBookApp = {
  projectId: number;
  companyName: string;
  realmId: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  connectedOn: string;
};

type QuickbookCardProps = {
  app: QuickBookApp;
  appConfig: AppConfigType;
  projectId: number;
  onDisconnect: () => void;
};

export const QuickbookCard = ({ appConfig, app, projectId, onDisconnect }: QuickbookCardProps) => {
  const [showConnectModal, toggleShowConnectModal] = useToggle(false);
  const routerQuery = useRouterQuery();
  const isRedirectedAndConnected = routerQuery.get(QUERY_PARAM_SUCCESS) || false;
  const [showIntegrationSuccessModal, toggleShowIntegrationSuccessModal] =
    useToggle(isRedirectedAndConnected);
  const location = useLocation();
  const history = useHistory();
  const disconnectQuery = useDisconnectQuickbooks();
  const [DisconnectModal, confirmDisconnect] = useConfirmDisconnect({
    message: `You are about to remove your ${appConfig.name} integration. If you disconnect the integration, it will no longer snyc over any data and you will no longer able to record new transactions to ${appConfig.shortName}.`,
  });
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const isInternalUser = checkAccess({ user: loggedUser.data, allowedRoles: INTERNAL_USER_ROLES });

  const handleDisconnect = useCallback(async () => {
    if (!(await confirmDisconnect())) {
      return;
    }

    const response = await disconnectQuery.mutateAsync({ projectId });

    if (response && onDisconnect) {
      onDisconnect();
    }

    return true;
  }, [projectId, disconnectQuery, onDisconnect, confirmDisconnect]);

  const handleConfirmIntegration = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has(QUERY_PARAM_SUCCESS)) {
      queryParams.delete(QUERY_PARAM_SUCCESS);
      history.replace({
        search: queryParams.toString(),
      });
    }

    toggleShowIntegrationSuccessModal();
  }, [history, location, toggleShowIntegrationSuccessModal]);

  return (
    <React.Fragment>
      {disconnectQuery.isLoading && <Spinner />}
      <AppCard
        appConfig={appConfig}
        onConnect={toggleShowConnectModal}
        onDisconnect={handleDisconnect}
        isConnected={!!app}
        showConnectButton={isInternalUser}
      >
        {app && (
          <div className="apps-card-body">
            <div className="form-container">
              <span>Account Name: </span>
              <span> {app.companyName} </span>
            </div>

            <div className="form-container">
              <span>Integration Date: </span>
              <span> {moment(app.connectedOn).format('DD-MMM-YYYY')} </span>
            </div>
          </div>
        )}
      </AppCard>

      {/* Display modal when connect button is clicked */}
      {showConnectModal && (
        <ModalPortal>
          <ConfirmModal
            onCancel={toggleShowConnectModal}
            confirmBtn={{
              link: getConnectQuickBooksUrl(projectId),
              label: `Connect to ${appConfig.shortName}`,
            }}
            title="Connect your account with your QuickBooks Online Account"
            confirmLabel="Connect to Quickbooks"
            classes="app-settings-modal"
          >
            <div className="mainContainer">
              <div className="boxContainer">
                <div
                  className="logoContainer"
                  style={{
                    backgroundImage: `url(/images/cloudcfo-logo-colored-icon-above-text.png)`,
                  }}
                ></div>
              </div>
              <div className="dashedLine"></div>
              <div className="boxContainer">
                <div
                  className="logoContainer"
                  style={{ backgroundImage: `url(/images/quickbooks-icon.png)` }}
                ></div>
              </div>
            </div>

            <div className="appSettingsContent">
              <p>How to connect</p>
              <ol>
                <li>
                  Click the <strong>Connect to QuickBooks button</strong> - you'll be redirected to
                  Quickbooks Online and prompted to log in.
                </li>
                <li>Follow the steps on screen.</li>
              </ol>
            </div>
          </ConfirmModal>
        </ModalPortal>
      )}

      {/* Display modal on app success connection */}
      {showIntegrationSuccessModal && (
        <ModalPortal>
          <ConfirmModal
            title="Integration Success!"
            confirmBtn={{
              label: 'Finish',
            }}
            message="You've completed everything you need to do. We'll now sync with your Chart of Accounts. When complete, you'll see the data populate under Lists in the main navigation."
            classes="success-modal"
            cancelBtn={false}
            onConfirm={handleConfirmIntegration}
            onCancel={handleConfirmIntegration}
          />
        </ModalPortal>
      )}
      {DisconnectModal && (
        <ModalPortal>
          <DisconnectModal />
        </ModalPortal>
      )}
    </React.Fragment>
  );
};
